import type { UploadedFile, WaybookAccount } from '../../types'
import { SingleFileUploader } from '../files/SingleFileUploader'
import { useToast } from '../ui/ToastProvider'

interface GeneralTabProps {
  formData: {
    title: string
    description: string
    privacy: 'public' | 'unlisted' | 'private'
    status: 'planning' | 'active' | 'completed' | 'archived'
    type: 'live' | 'shared' | 'personal'
    tags: string
    startDate: string
    endDate: string
    coverImage: string
    coverImageFileId?: string
    userId: string
    createdBy: string
    verified: boolean
    isTemplate: boolean
    originalTripId: string
  }
  setFormData: (data: any) => void
  waybookAccounts: WaybookAccount[]
  isNewTrip: boolean
  tripId?: string
}

export default function GeneralTab({ formData, setFormData, waybookAccounts, isNewTrip, tripId }: GeneralTabProps) {
  const { showToast } = useToast()
  const handleChange = (field: string, value: any) => {
    if (field === 'userId') {
      console.log('🔄 Changing userId to:', value)
    }
    setFormData((prev: any) => ({ ...prev, [field]: value }))
  }

  console.log('📋 GeneralTab render:', {
    isNewTrip,
    userId: formData.userId,
    createdBy: formData.createdBy,
    hasAccounts: waybookAccounts.length,
  })

  const coverImageValue: UploadedFile | null = formData.coverImage
    ? {
        id: formData.coverImageFileId || 'cover-image',
        url: formData.coverImage,
        thumbnailUrl: formData.coverImage,
        fileType: 'image',
        size: 0,
        filename: formData.coverImage.split('/').pop() || 'cover-image',
        originalFilename: formData.coverImage.split('/').pop() || undefined,
      }
    : null

  // For existing trips, show the creator (createdBy)
  // For new trips, allow selecting who publishes it (userId)
  const displayUserId = isNewTrip ? formData.userId : formData.createdBy
  const creatorAccount = waybookAccounts.find(acc => acc.userId === displayUserId)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isNewTrip ? 'Publish as *' : 'Created by (Original Creator)'}
            </label>
            {isNewTrip ? (
              <select
                value={formData.userId}
                onChange={(e) => handleChange('userId', e.target.value)}
                className="input w-full"
                required
              >
                <option value="">Select Account</option>
                {waybookAccounts.map((account) => (
                  <option key={account.userId} value={account.userId}>
                    {account.label} ({account.region})
                  </option>
                ))}
              </select>
            ) : (
              <div className="input w-full bg-gray-50 flex items-center">
                {creatorAccount ? `${creatorAccount.label} (${creatorAccount.region})` : formData.createdBy || 'Unknown'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="input w-full"
              placeholder="Enter trip title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="input w-full min-h-[150px]"
              placeholder="Describe your trip..."
            />
          </div>

          <div>
            <SingleFileUploader
              label="Cover Image"
              helperText={
                isNewTrip && !formData.userId
                  ? "⚠️ Please select 'Publish as' account above before uploading cover image"
                  : "Upload a hero image for the trip (recommended 1600×900)."
              }
              value={coverImageValue}
              onChange={(file) => {
                if (isNewTrip && !formData.userId) {
                  showToast('Please select a "Publish as" account before uploading a cover image.', {
                    type: 'error',
                  })
                  return
                }
                handleChange('coverImage', file?.url || '')
                handleChange('coverImageFileId', file?.id || '')
              }}
              tripId={tripId}
              userId={formData.userId || formData.createdBy}
              privacy="public"
              disabled={isNewTrip && !formData.userId}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="input w-full"
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Privacy
              </label>
              <select
                value={formData.privacy}
                onChange={(e) => handleChange('privacy', e.target.value)}
                className="input w-full"
              >
                <option value="public">Public</option>
                <option value="unlisted">Unlisted</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="input w-full"
              >
                <option value="live">Live</option>
                <option value="shared">Shared</option>
                <option value="personal">Personal</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                className="input w-full"
                min={formData.startDate}
              />
            </div>
          </div>


          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="verified"
                checked={formData.verified}
                onChange={(e) => handleChange('verified', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="verified" className="text-sm font-medium text-gray-700">
                Verified Trip ✓
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isTemplate"
                checked={formData.isTemplate}
                onChange={(e) => handleChange('isTemplate', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isTemplate" className="text-sm font-medium text-gray-700">
                Is Template
              </label>
            </div>
          </div>

          {formData.isTemplate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Original Trip ID (if copied from)
              </label>
              <input
                type="text"
                value={formData.originalTripId}
                onChange={(e) => handleChange('originalTripId', e.target.value)}
                className="input w-full"
                placeholder="trip_xxx"
              />
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Field Descriptions</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><strong>Status:</strong> Planning (default) → Active → Completed → Archived</li>
              <li><strong>Privacy:</strong> Public (visible to all) | Unlisted (link only) | Private (participants only)</li>
              <li><strong>Type:</strong> Live (real-time) | Shared (multi-user) | Personal (solo)</li>
              <li><strong>Verified:</strong> Mark as official/verified trip</li>
              <li><strong>Template:</strong> Can be used as a template by others</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
